import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { CollectionSelectionController } from '../types';
import {
  collectionSelectionReducer,
  initialCollectionSelectionState,
} from '../state/collectionSelectionReducer';
import {
  playSelectionBeep,
  playSelectionBeepCascade,
} from '../../../services/selectionAudio';

export function useCollectionSelection(
  validIds: string[],
  disabled = false,
): CollectionSelectionController {
  const [state, dispatch] = useReducer(
    collectionSelectionReducer,
    initialCollectionSelectionState,
  );
  const validIdsKey = validIds.join('\u0000');
  const previousSelectedIdsRef = useRef<Set<string>>(state.selectedIds);

  useEffect(() => {
    const prev = previousSelectedIdsRef.current;
    const current = state.selectedIds;
    previousSelectedIdsRef.current = current;

    if (current.size > 0) {
      let newlyAddedCount = 0;
      for (const id of current) {
        if (!prev.has(id)) {
          newlyAddedCount++;
        }
      }
      if (newlyAddedCount === 1) {
        playSelectionBeep(current.size - 1);
      } else if (newlyAddedCount > 1) {
        playSelectionBeepCascade(newlyAddedCount);
      }
    }
  }, [state.selectedIds]);

  useEffect(() => {
    dispatch({ type: 'reconcile', validIds });
    // The stable key prevents subscription-array identity from causing reducer churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validIdsKey]);

  useEffect(() => {
    if (disabled) dispatch({ type: 'clear' });
  }, [disabled]);

  const enter = useCallback(() => dispatch({ type: 'enter' }), []);
  const toggle = useCallback(
    (id: string, options?: { shiftKey?: boolean; visibleIds?: string[] }) => {
      if (!validIds.includes(id)) return;
      dispatch({
        type: 'toggle',
        id,
        shiftKey: options?.shiftKey,
        visibleIds: options?.visibleIds?.filter((entry) =>
          validIds.includes(entry),
        ),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validIdsKey],
  );
  const selectRange = useCallback(
    (targetId: string, visibleIds: string[]) => {
      if (!validIds.includes(targetId)) return;
      dispatch({
        type: 'selectRange',
        targetId,
        visibleIds: visibleIds.filter((id) => validIds.includes(id)),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validIdsKey],
  );
  const toggleVisible = useCallback(
    (ids: string[]) =>
      dispatch({
        type: 'toggleVisible',
        ids: ids.filter((id) => validIds.includes(id)),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validIdsKey],
  );
  const select = useCallback(
    (ids: string[]) =>
      dispatch({
        type: 'select',
        ids: ids.filter((id) => validIds.includes(id)),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validIdsKey],
  );
  const clear = useCallback(() => dispatch({ type: 'clear' }), []);
  const isSelected = useCallback(
    (id: string) => state.selectedIds.has(id),
    [state.selectedIds],
  );

  return useMemo(
    () => ({
      disabled,
      active: !disabled && state.active,
      selectedIds: disabled ? new Set<string>() : state.selectedIds,
      lastSelectedId: state.lastSelectedId,
      enter: disabled ? () => {} : enter,
      toggle: disabled ? () => {} : toggle,
      selectRange: disabled ? () => {} : selectRange,
      toggleVisible: disabled ? () => {} : toggleVisible,
      select: disabled ? () => {} : select,
      clear,
      isSelected: disabled ? () => false : isSelected,
    }),
    [
      state,
      disabled,
      enter,
      toggle,
      selectRange,
      toggleVisible,
      select,
      clear,
      isSelected,
    ],
  );
}
